import { useState, useEffect, useRef } from 'react';
import { MonitorSnapshot, MonitorPosition } from '../../../types/monitor.types';
import { AvatarState, AvatarEmotionalState } from '../types/tradingFloor.types';
import { DESK_CONFIGS, TILE_SIZE, resolveModelName } from '../constants/officeLayout';

const SLUMP_DURATION_MS = 60_000; // 60 seconds of slumping after stop-out
const WIN_CELEBRATION_MS = 15_000; // 15 seconds of celebration after profitable close

/**
 * Aggregate multiple DCA positions into a single synthetic position.
 * Sums P/L, weighted-averages R by size, takes max tier, earliest entry time.
 */
function aggregatePositions(positions: MonitorPosition[]): MonitorPosition {
  if (positions.length === 1) return positions[0];

  const totalPL = positions.reduce((sum, p) => sum + p.currentPL, 0);
  const totalSize = positions.reduce((sum, p) => sum + p.size, 0);
  const weightedR = totalSize > 0
    ? positions.reduce((sum, p) => sum + p.currentR * p.size, 0) / totalSize
    : positions.reduce((sum, p) => sum + p.currentR, 0) / positions.length;
  const maxTier = Math.max(...positions.map(p => p.tier));
  const totalMaxProfit = positions.reduce((sum, p) => sum + p.maxProfit, 0);
  const totalProtectedProfit = positions.reduce((sum, p) => sum + p.protectedProfit, 0);

  // Find earliest entry
  let earliest = positions[0];
  for (let i = 1; i < positions.length; i++) {
    if (new Date(positions[i].entryTime).getTime() < new Date(earliest.entryTime).getTime()) {
      earliest = positions[i];
    }
  }

  return {
    ...positions[0],
    currentPL: totalPL,
    currentR: weightedR,
    tier: maxTier,
    size: totalSize,
    entryTime: earliest.entryTime,
    entryPrice: earliest.entryPrice,
    maxProfit: totalMaxProfit,
    protectedProfit: totalProtectedProfit,
    dcaEntriesFilled: positions.length,
    dcaMaxEntries: positions[0].dcaMaxEntries ?? positions.length,
  };
}

function deriveEmotionalState(
  position: MonitorPosition | null,
  previousPosition: MonitorPosition | null,
  slumpedSince: number | null,
  winSince: number | null
): AvatarEmotionalState {
  if (!position) {
    // Trade just closed — was it a win or loss?
    if (previousPosition) {
      if (previousPosition.currentPL >= 0 && winSince) {
        const elapsed = Date.now() - winSince;
        if (elapsed < WIN_CELEBRATION_MS) return 'celebrating_win';
      } else if (slumpedSince) {
        const elapsed = Date.now() - slumpedSince;
        if (elapsed < SLUMP_DURATION_MS) return 'slumped';
      }
    }
    return 'idle_typing';
  }

  // Trade just placed (within last 60 seconds)
  const entryAge = Date.now() - new Date(position.entryTime).getTime();
  if (entryAge < 60_000) return 'alert_standing';

  // Big profit
  if (position.tier >= 2 || position.currentR >= 2.0) return 'celebrating_big';
  if (position.tier >= 1 || position.currentR >= 1.0) return 'celebrating';
  if (position.currentR > 0) return 'happy_working';

  // Losing
  if (position.currentR < -0.5) return 'stressed_heavy';
  if (position.currentR < 0) return 'stressed';

  return 'idle_typing';
}

export function useAvatarStates(snapshot: MonitorSnapshot | null): AvatarState[] {
  const prevPositionsRef = useRef<Map<string, MonitorPosition>>(new Map());
  const slumpTimesRef = useRef<Map<string, number>>(new Map());
  const winTimesRef = useRef<Map<string, number>>(new Map());
  const [avatarStates, setAvatarStates] = useState<AvatarState[]>(() =>
    DESK_CONFIGS.map(desk => ({
      modelName: desk.modelName,
      displayName: desk.displayName,
      emotionalState: 'idle_typing' as AvatarEmotionalState,
      position: null,
      previousPosition: null,
      allPositions: [],
      deskX: desk.gridX * TILE_SIZE,
      deskY: desk.gridY * TILE_SIZE,
      paletteKey: desk.paletteKey,
      slumpedSince: null,
    }))
  );

  useEffect(() => {
    if (!snapshot) return;

    // Build a map of model name -> ALL positions from snapshot (DCA = multiple entries)
    const positionGroupsByModel = new Map<string, MonitorPosition[]>();
    for (const pos of snapshot.positions) {
      const resolvedName = resolveModelName(pos.model);
      const existing = positionGroupsByModel.get(resolvedName) || [];
      existing.push(pos);
      positionGroupsByModel.set(resolvedName, existing);
    }

    const newStates = DESK_CONFIGS.map(desk => {
      const allModelPositions = positionGroupsByModel.get(desk.modelName) || [];
      const currentPosition = allModelPositions.length > 0 ? aggregatePositions(allModelPositions) : null;
      const previousPosition = prevPositionsRef.current.get(desk.modelName) || null;

      // Track slump/win timing
      let slumpedSince = slumpTimesRef.current.get(desk.modelName) || null;
      let winSince = winTimesRef.current.get(desk.modelName) || null;

      if (!currentPosition && previousPosition && !slumpedSince && !winSince) {
        // Just lost a position — determine if win or loss
        if (previousPosition.currentPL >= 0) {
          // Profitable close → win celebration
          winSince = Date.now();
          winTimesRef.current.set(desk.modelName, winSince);
        } else {
          // Loss → slump
          slumpedSince = Date.now();
          slumpTimesRef.current.set(desk.modelName, slumpedSince);
        }
      } else if (currentPosition) {
        // Has a position → clear both timers
        slumpTimesRef.current.delete(desk.modelName);
        winTimesRef.current.delete(desk.modelName);
        slumpedSince = null;
        winSince = null;
      } else if (slumpedSince && Date.now() - slumpedSince >= SLUMP_DURATION_MS) {
        // Slump expired
        slumpTimesRef.current.delete(desk.modelName);
        slumpedSince = null;
      } else if (winSince && Date.now() - winSince >= WIN_CELEBRATION_MS) {
        // Win celebration expired
        winTimesRef.current.delete(desk.modelName);
        winSince = null;
      }

      const emotionalState = deriveEmotionalState(currentPosition, previousPosition, slumpedSince, winSince);

      return {
        modelName: desk.modelName,
        displayName: desk.displayName,
        emotionalState,
        position: currentPosition,
        previousPosition,
        allPositions: allModelPositions,
        deskX: desk.gridX * TILE_SIZE,
        deskY: desk.gridY * TILE_SIZE,
        paletteKey: desk.paletteKey,
        slumpedSince,
      };
    });

    // Update previous positions ref (store aggregated positions)
    const newPrev = new Map<string, MonitorPosition>();
    positionGroupsByModel.forEach((positions, modelName) => {
      newPrev.set(modelName, aggregatePositions(positions));
    });
    prevPositionsRef.current = newPrev;

    setAvatarStates(newStates);
  }, [snapshot]);

  return avatarStates;
}
